import { useState } from 'react';
import { api, ApiError } from '../api.ts';

/** The same as the phone's sheet and the API (routes/feedback.ts). */
export const FEEDBACK_MAX = 500;
export const FEEDBACK_CATEGORIES = [
  ['reminders', 'Reminders'],
  ['medicines', 'Medicines'],
  ['caregivers', 'Caregivers'],
  ['languages', 'Languages'],
  ['other', 'Something else'],
] as const;

/**
 * "Send feedback" — the owner's request of 4 October 2026.
 *
 * The phone's "Tell us what to improve", on care.drbell.app: a few
 * sentences, an optional category, and whether to include the browser and
 * the site's version. Into the same Feedback inbox in the admin console
 * (POST /api/feedback). English here, as the rest of this site is.
 */
export function Feedback({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [includeDevice, setIncludeDevice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/feedback', {
        text: text.trim(),
        category,
        source: 'care',
        platform: 'web',
        includeDevice,
        // The site has no version number of its own: which site it was is what helps.
        appVersion: includeDevice ? 'care.drbell.app' : null,
        // The browser, in a word or two: never the full user agent string.
        deviceModel: includeDevice ? browserName(navigator.userAgent) : null,
        locale: navigator.language || null,
      });
      setSent(true);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? 'You have sent a lot today. Please try again tomorrow.'
          : 'Could not send. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Send feedback</h2>
        <p role="status">Thank you. We read every message.</p>
        <button onClick={onClose} className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white">Done</button>
      </div>
    );
  }

  const left = FEEDBACK_MAX - text.length;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim() && !busy) void send();
      }}
    >
      <h2 className="text-lg font-semibold">Send feedback</h2>
      <p className="text-sm text-(--muted)">Tell us what to improve. A person on the DrBell team reads every message.</p>
      <label className="block">
        <span className="text-sm font-medium">Your message</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, FEEDBACK_MAX))}
          maxLength={FEEDBACK_MAX}
          rows={5}
          required
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 text-sm outline-none focus:border-(--accent)"
        />
        <span className="text-xs text-(--muted)" aria-live="polite">{left} characters left</span>
      </label>
      <fieldset>
        <legend className="text-sm font-medium">What is it about? (optional)</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {FEEDBACK_CATEGORIES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={category === value}
              onClick={() => setCategory(category === value ? null : value)}
              className={`min-h-11 rounded-full border px-3 text-sm ${
                category === value ? 'border-(--accent) bg-(--accent-soft) text-(--accent)' : 'border-(--line) hover:border-(--accent)'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <input type="checkbox" checked={includeDevice} onChange={(e) => setIncludeDevice(e.target.checked)} className="h-5 w-5" />
        Include my browser and the site’s version
      </label>
      <p className="text-xs text-(--muted)">Please do not write medicines or anything about your health.</p>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={busy || !text.trim()}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button type="button" onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

/** "Chrome", "Safari", "Firefox", "Edge" or "Browser": enough to reproduce, nothing that fingerprints. */
export function browserName(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Browser';
}
